/*
  Store Builder craft: the colour picker (rounds 5 to 15; 19 and 20 Oct 2026; store-kit/README.md, "Craft hooks").
  Post-mortem, 20 Oct 2026 (the owner's Loom, all four live stores: "the thumbnails disappear when I click anything";
  on Grayson Wells, scrolled down to the swatches, a colour pick "pulls me to the top"). What happened: after a pick the
  gallery showed the colour's one photo with no thumbnail strip, and the page was pulled back up. Root causes: F1
  (16 Oct) gave each colour only its own photos, and StoreOps shoots one photo for every colour but the first, so a pick
  left one thumbnail, which this script hid at the click (round 13, data-sb-hid and data-sb-gone: "never one alone")
  and a phone folded away. To keep the swatches under the finger it also scrolled the page (the window, or Horizon's
  .page-wrapper on a computer) at the click and again when Horizon's new gallery came in, by however far the swatches
  had moved since the click: a shopper still scrolling after a pick (reading on, or a trackpad's glide) was pulled back
  by their own scroll, every time (measured headless on Grayson: 300px down after a pick, back up 300px at 440ms).
  Rule now: the gallery holds every photo of the product on every colour (snippets/product-media-gallery-content.liquid),
  so no strip ever shrinks, hides or folds; a pick moves only the gallery's own carousel (Horizon's Slideshow.select,
  which scrolls its slide scroller and its thumbnail strip, never the page). The kit never scrolls the window or the
  page's scroller on a colour or size pick (pinned by render/craft-gallery-never-hides.test.ts).
  Deferred, after store-builder-craft-colour-photo.js. No network, no markup from strings. On a colour pick:
  - the legend names the colour at once;
  - the carousel slides to the colour's photo (the radio's data-sb-media, snippets/variant-main-picker.liquid) and marks
    its thumbnail, as a thumbnail click does; a far pick Horizon drops mid-slide is taken up once that slide has landed,
    unless the shopper has touched the gallery since;
  - Horizon's answer holds the same photos, so its new gallery is not swapped in (below) and the carousel stays where
    the pick or the shopper left it. A size pick keeps the gallery likewise.
  H1: with a fine pointer and --sb-colour-preview: hover, a tile's photo shows over the main one while the pointer rests on it.
*/
(() => {
if (window.sbColourName) return;
window.sbColourName = true;

const doc = document;
const COLOUR = /colou?r|shade|tone/i;
// The colour's photo fetched ahead and its hover layer: store-builder-craft-colour-photo.js, deferred just before this one.
const { ready, main, fetched, lay } = window.sbColourPhoto || {};

// The carousel's slides as Horizon counts them (Slideshow.slides: those not hidden), and the photo each holds.
const slidesOf = (show) => [...show.querySelectorAll('slideshow-slide')]
  .filter((slide) => slide.closest('slideshow-component') === show && !slide.hidden);
const photoOf = (slide) => slide.querySelector('[data-media-id]')?.getAttribute('data-media-id');

// The colour's photo into view in the gallery's carousel, by Horizon's own select: the carousel slides, the page stays.
const wanted = new WeakMap();
function goTo(gallery, id) {
  const show = gallery?.querySelector('slideshow-component');
  if (!id || typeof show?.select !== 'function') return;
  wanted.set(show, id);
  if (!show.sbWatched) {
    show.sbWatched = true;
    show.addEventListener('pointerdown', () => wanted.delete(show), { passive: true }); // the shopper's own swipe or tap
  }
  const move = () => {
    const slides = slidesOf(show);
    const at = slides.findIndex((slide) => photoOf(slide) === id);
    if (at < 0 || wanted.get(show) !== id || !show.isConnected) return;
    for (const img of slides[at].querySelectorAll('img')) img.loading = 'eager';
    if (show.current !== at) show.select(at);
  };
  move();
  setTimeout(move, 600); // Horizon ignores a select while a far one still slides (Slideshow.select's loop)
}

// H1: a colour's photo while the pointer rests on its tile.
let peeking = null;
function peek(input) {
  if (peeking === input) return;
  peeking = input;
  for (const layer of doc.querySelectorAll('.sb-peek')) layer.remove();
  const section = input?.closest('.shopify-section');
  const gallery = section?.querySelector('media-gallery');
  const shown = gallery && main(gallery);
  const ahead = section && fetched(section, 'data_sb_colour', input.value);
  if (shown && ready(ahead) && gallery.parentElement) lay(shown, ahead, gallery, 'sb-swap sb-peek');
}
if (main && doc.documentElement.getAttribute('data-sb-colour-preview') === 'hover' && window.matchMedia?.('(hover: hover) and (pointer: fine)').matches) {
  doc.addEventListener('pointerover', ({ target }) => {
    const input = target.closest?.('.variant-option__button-label--has-swatch')?.querySelector('input');
    peek(input && !input.checked && COLOUR.test(input.dataset.optionName || '') ? input : null);
  });
}

// The same photos (paths, in any order) as the gallery showing: Horizon's new gallery for a pick is not swapped in. Its
// answer leads with the chosen colour's photo, which the carousel already shows (moved at the click); swapping it in
// would send the carousel back to its first photo and rebuild the strip under the shopper. Other photos are swapped.
const photos = (gallery) => [...gallery.querySelectorAll('slideshow-slide img')]
  .map((img) => (img.getAttribute('src') || '').split('?')[0]).sort().join(' ');
customElements.whenDefined('media-gallery').then(() => {
  const proto = customElements.get('media-gallery')?.prototype;
  if (!proto || proto.sbKeep) return;
  const replace = proto.replaceWith;
  proto.sbKeep = true;
  proto.replaceWith = function replaceWith(...nodes) {
    const [next] = nodes;
    const same = nodes.length === 1 && next?.localName === 'media-gallery' && photos(next) !== '' && photos(next) === photos(this);
    return same ? undefined : replace.apply(this, nodes);
  };
});

doc.addEventListener('change', ({ target }) => {
  if (!(target instanceof HTMLInputElement) || target.type !== 'radio' || !target.checked) return;
  const picker = target.closest('variant-picker fieldset');
  const name = picker?.querySelector('legend .variant-option__swatch-value');
  if (name) name.textContent = target.value;
  if (!picker || target.closest('product-card') || !COLOUR.test(target.dataset.optionName || '')) return;
  const box = target.closest('dialog, .shopify-section');
  for (const layer of box?.querySelectorAll('.sb-peek') ?? []) setTimeout(() => layer.remove(), 600); // once slid under
  goTo(box?.querySelector('media-gallery'), target.getAttribute('data-sb-media'));
});
})();
